import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { toast } from 'sonner'

interface User {
  id: number
  name: string
  email: string
  referralCode?: string
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string, referral?: string) => Promise<void>
  logout: () => void
  updateProfile: (name: string) => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

const API_URL = import.meta.env.VITE_API_URL || '/api'

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      fetchUser(token)
    } else {
      setIsLoading(false)
    }
    
    // Refresh auth state when navigating back
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const token = localStorage.getItem('token')
        if (token) {
          fetchUser(token)
        }
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const fetchUser = async (token: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
      } else {
        localStorage.removeItem('token')
      }
    } catch (error) {
      console.error('Failed to fetch user:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      })
      
      if (!response.ok) {
        const text = await response.text()
        let message = 'Login gagal'
        try {
          const error = JSON.parse(text)
          message = error.error || message
        } catch {
          if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            message = 'Server sedang tidak tersedia. Coba lagi beberapa saat.'
          }
        }
        throw new Error(message)
      }
      
      const data = await response.json()
      localStorage.setItem('token', data.token)
      setUser(data.user)
      toast.success('Login berhasil!')
    } catch (error: any) {
      toast.error(error.message)
      throw error
    }
  }

  const register = async (name: string, email: string, password: string, referral?: string) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          password,
          referral: referral?.trim() || undefined,
        })
      })
      
      if (!response.ok) {
        const text = await response.text()
        let message = 'Registrasi gagal'
        try {
          const error = JSON.parse(text)
          message = error.error || message
        } catch {
          if (text.includes('<!DOCTYPE') || text.includes('<html')) {
            message = 'Server sedang tidak tersedia. Coba lagi beberapa saat.'
          }
        }
        throw new Error(message)
      }
      
      const data = await response.json()
      localStorage.setItem('token', data.token)
      setUser(data.user)
      toast.success('Registrasi berhasil!')
    } catch (error: any) {
      toast.error(error.message)
      throw error
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
    toast.success('Logged out successfully')
  }

  const updateProfile = async (name: string) => {
    const token = localStorage.getItem('token')
    if (!token) throw new Error('Unauthorized')

    const response = await fetch(`${API_URL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ name }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Gagal update profil')
    }

    const updatedUser = await response.json()
    setUser(updatedUser)
    toast.success('Profil berhasil diperbarui')
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const token = localStorage.getItem('token')
    if (!token) throw new Error('Unauthorized')

    const response = await fetch(`${API_URL}/auth/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Gagal mengubah password')
    }

    toast.success('Password berhasil diubah')
  }

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
